/**
 * Media Utilities for Creatives Module
 * Consolidates common media processing, validation, and manipulation functions
 */

import { Storage } from "aws-amplify";
import { message } from "antd";

/**
 * Transforms filename to be S3-compatible
 * @param {string} originalFileName - Original file name
 * @returns {string} - Transformed filename
 */
export const transformFileName = originalFileName => {
  const fileParts = originalFileName.split(".");
  const fileExtension = fileParts.pop();
  const fileNameWithoutExtension = fileParts.join(".");

  // Remove spaces and replace with underscore
  let fileNameWithoutSpaces = fileNameWithoutExtension.replace(/\s+/g, "_");

  // Remove non-alphanumeric characters
  let alphanumericFileName = fileNameWithoutSpaces.replace(
    /[^a-zA-Z0-9_]/g,
    ""
  );

  // Convert to lowercase
  alphanumericFileName = alphanumericFileName.toLowerCase();

  // Add a date stamp in Unix format with milliseconds
  const currentDate = new Date();
  const unixTimestamp = currentDate.getTime();
  const formattedFileName = `${alphanumericFileName}_${unixTimestamp}.${fileExtension}`;

  return formattedFileName;
};

/**
 * Gets filename without extension
 * @param {string} fileName - File name with extension
 * @returns {string} - Filename without extension
 */
export const getFileNameWithoutExtension = fileName => {
  const parts = fileName.split(".");
  parts.pop();
  return parts.join(".");
};

/**
 * Uploads file to S3 storage with size validation
 * @param {File} file - File to upload
 * @param {string} contentType - Content type of the file
 * @returns {Promise<{url: string, key: string}>} - Upload response
 */
export const uploadToS3Storage = async (file, contentType = "image/jpeg") => {
  const fileName = transformFileName(file.name);
  
  // Enforce strict file size limit - 5MB max for reliable uploads
  const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
  
  if (file.size > MAX_FILE_SIZE) {
    const fileSizeMB = (file.size / 1024 / 1024).toFixed(1);
    const errorMessage = `File size (${fileSizeMB}MB) exceeds the 5MB limit. Please compress your file or use a smaller file.`;
    message.error(errorMessage);
    throw new Error(errorMessage);
  }
  
  try {
    const response = await Storage.put(fileName, file, {
      contentType: contentType,
    });

    const url = await Storage.get(response.key, { 
      expires: 600000 // 10 minutes expiry time
    });

    return {
      url: url,
      key: response.key,
    };

  } catch (error) {
    const errorMessage = `Upload failed: ${error.message}`;
    message.error(errorMessage);
    throw new Error(errorMessage);
  }
};

/**
 * Gets video duration from file
 * @param {File} file - Video file
 * @returns {Promise<number>} - Video duration in seconds
 */
export const getVideoDuration = file => {
  return new Promise((resolve, reject) => {
    if (!file) {
      resolve(0);
      return;
    }

    const videoElement = document.createElement("video");
    videoElement.src = URL.createObjectURL(file);

    videoElement.onloadedmetadata = function() {
      URL.revokeObjectURL(videoElement.src);
      resolve(videoElement.duration);
    };

    videoElement.onerror = function() {
      URL.revokeObjectURL(videoElement.src);
      reject(new Error("Failed to load video metadata"));
    };
  });
};

/**
 * Gets comprehensive video metadata from file
 * @param {File} file - Video file
 * @returns {Promise<{width: number, height: number, duration: number, bitrate: number, hasIssues: boolean, issues: string[]}>} - Video metadata
 */
export const getVideoMetadata = file => {
  return new Promise(resolve => {
    if (!file) {
      resolve({ 
        width: 0, 
        height: 0, 
        duration: 0, 
        bitrate: 0, 
        hasIssues: false, 
        issues: [] 
      });
      return;
    }

    const videoElement = document.createElement("video");
    videoElement.src = URL.createObjectURL(file);

    videoElement.onloadedmetadata = function() {
      const duration = videoElement.duration;
      const dimensions = {
        width: videoElement.videoWidth,
        height: videoElement.videoHeight,
      };
      
      // Calculate approximate bitrate (bits per second)
      const bitrate = duration > 0 ? (file.size * 8) / duration : 0;
      const bitrateKbps = bitrate / 1000;
      
      // Check for potential issues
      const issues = [];
      let hasIssues = false;
      
      // File size check (main issue)
      if (file.size > 5 * 1024 * 1024) {
        issues.push(`File too large: ${(file.size / 1024 / 1024).toFixed(1)}MB (max 5MB)`);
        hasIssues = true;
      }
      
      // High bitrate can indicate large file
      if (bitrateKbps > 8000) { // > 8 Mbps
        issues.push(`High bitrate: ${Math.round(bitrateKbps)} kbps (consider compressing)`);
        hasIssues = true;
      }
      
      // Very high resolution can cause large files
      if (dimensions.width > 1920 || dimensions.height > 1080) { // > 1080p
        issues.push(`High resolution: ${dimensions.width}x${dimensions.height} (consider 1080p or lower)`);
        hasIssues = true;
      }
      
      URL.revokeObjectURL(videoElement.src);
      resolve({
        width: dimensions.width,
        height: dimensions.height,
        duration,
        bitrate: bitrateKbps,
        hasIssues,
        issues
      });
    };

    videoElement.onerror = function() {
      URL.revokeObjectURL(videoElement.src);
      resolve({
        width: 0,
        height: 0,
        duration: 0,
        bitrate: 0,
        hasIssues: true,
        issues: ['Failed to load video metadata - file may be corrupted']
      });
    };
  });
};

/**
 * Gets video dimensions from file (legacy compatibility)
 * @param {File} file - Video file
 * @returns {Promise<{width: number, height: number}>} - Video dimensions
 */
export const getVideoDimensions = async file => {
  const metadata = await getVideoMetadata(file);
  return {
    width: metadata.width,
    height: metadata.height,
  };
};

/**
 * Gets image dimensions from file
 * @param {File} file - Image file
 * @returns {Promise<{width: number, height: number}>} - Image dimensions
 */
export const getImageDimensions = file => {
  return new Promise(resolve => {
    if (!file) {
      resolve({ width: 0, height: 0 });
      return;
    }

    const img = new Image();
    img.src = URL.createObjectURL(file);

    img.onload = function() {
      const dimensions = {
        width: img.naturalWidth,
        height: img.naturalHeight,
      };
      URL.revokeObjectURL(img.src);
      resolve(dimensions);
    };

    img.onerror = function() {
      URL.revokeObjectURL(img.src);
      resolve({ width: 0, height: 0 });
    };
  });
};

/**
 * Validates file size with clear messaging
 * @param {File} file - File to validate
 * @param {number} maxSizeMB - Maximum size in MB (default 5MB)
 * @returns {boolean} - Whether file size is valid
 */
export const validateFileSize = (file, maxSizeMB = 5) => {
  const maxSizeBytes = maxSizeMB * 1024 * 1024;
  if (file.size > maxSizeBytes) {
    const fileSizeMB = (file.size / 1024 / 1024).toFixed(1);
    message.error(`File size (${fileSizeMB}MB) exceeds the ${maxSizeMB}MB limit. Please compress your file.`);
    return false;
  }
  return true;
};

/**
 * Validates image dimensions against allowed dimensions
 * @param {number} width - Image width
 * @param {number} height - Image height
 * @param {Array} validDimensions - Array of valid dimension objects
 * @returns {boolean} - Whether dimensions are valid
 */
export const validateImageDimensions = (
  width,
  height,
  validDimensions = []
) => {
  if (!validDimensions.length) return true;

  return validDimensions.some(
    dim => dim.width === width && dim.height === height
  );
};

/**
 * Validates video duration
 * @param {number} duration - Duration in seconds
 * @param {number} minDuration - Minimum duration in seconds
 * @param {number} maxDuration - Maximum duration in seconds
 * @returns {boolean} - Whether duration is valid
 */
export const validateVideoDuration = (
  duration,
  minDuration = 15,
  maxDuration = 30
) => {
  return duration >= minDuration && duration <= maxDuration;
};

/**
 * Processes media files by renewing S3 URLs
 * @param {Array} medias - Array of media objects
 * @returns {Promise<Array>} - Processed media array
 */
export const processMediasWithUrls = async medias => {
  if (!medias || !Array.isArray(medias)) return [];

  const processedMedias = await Promise.all(
    medias.map(async media => {
      try {
        const response = await Storage.get(media.key, { expires: 600000 });
        return {
          ...media,
          url: response,
        };
      } catch (error) {
        return media;
      }
    })
  );

  return processedMedias;
};

/**
 * Filters ad groups by media type
 * @param {Array} adGroups - Array of ad group objects
 * @param {string} mediaType - Media type to filter by ("VIDEO" or other)
 * @returns {Array} - Filtered ad groups
 */
export const filterAdGroupsByMediaType = (adGroups, mediaType) => {
  if (!adGroups || !Array.isArray(adGroups)) return [];

  return adGroups.filter(group => {
    if (!Array.isArray(group.medias)) return false;

    if (mediaType === "VIDEO") {
      return group.medias.some(
        media => media.type && media.type.toUpperCase() === "VIDEO"
      );
    } else {
      return group.medias.some(
        media => media.type && media.type.toUpperCase() !== "VIDEO"
      );
    }
  });
};

/**
 * Filters ad groups by media type compatibility for assignment
 * Returns ad groups that can accept the specified media type without conflicts
 * @param {Array} adGroups - Array of ad group objects
 * @param {string} mediaType - Media type to check compatibility for ("VIDEO" or other)
 * @returns {Array} - Filtered ad groups that are compatible with the media type
 */
export const filterAdGroupsByMediaCompatibility = (adGroups, mediaType) => {
  if (!adGroups || !Array.isArray(adGroups)) return [];

  return adGroups.filter(group => {
    // Empty ad groups (no medias array or empty array) can accept any media type
    if (!Array.isArray(group.medias) || group.medias.length === 0) {
      return true;
    }

    if (mediaType === "VIDEO") {
      // For video: show ad groups that don't contain non-video media
      return !group.medias.some(
        media =>
          (media?.type && media.type.toUpperCase() !== "VIDEO") ||
          (media?.fileType && media.fileType.toUpperCase() !== "MP4")
      );
    } else {
      // For display/image: show ad groups that don't contain video media
      return !group.medias.some(
        media =>
          (media?.type && media.type.toUpperCase() === "VIDEO") ||
          (media?.fileType && media.fileType.toUpperCase() === "MP4")
      );
    }
  });
};

/**
 * Capitalizes first letter of a string
 * @param {string} str - String to capitalize
 * @returns {string} - Capitalized string
 */
export const capitalizeFirstLetter = str => {
  if (!str || typeof str !== "string") return str;
  return str.charAt(0).toUpperCase() + str.slice(1);
};

/**
 * Formats file size in human-readable format
 * @param {number} bytes - File size in bytes
 * @returns {string} - Formatted file size
 */
export const formatFileSize = bytes => {
  if (bytes === 0) return "0 Bytes";

  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
};

/**
 * Common error handler for media operations
 * @param {Error} error - Error object
 * @param {string} operation - Operation that failed
 * @param {string} defaultMessage - Default error message
 */
export const handleMediaError = (error, operation, defaultMessage) => {
  if (error.message) {
    message.error(`${operation} failed: ${error.message}`);
  } else {
    message.error(defaultMessage || `${operation} failed`);
  }
};

/**
 * Common success handler for media operations
 * @param {string} operation - Operation that succeeded
 * @param {number} count - Number of items processed
 */
export const handleMediaSuccess = (operation, count = 1) => {
  const itemText = count === 1 ? "item" : "items";
  message.success(
    `${operation} completed successfully! ${count} ${itemText} processed.`
  );
};

/**
 * Debounce function for search operations
 * @param {Function} func - Function to debounce
 * @param {number} delay - Delay in milliseconds
 * @returns {Function} - Debounced function
 */
export const debounce = (func, delay) => {
  let timeoutId;
  return function(...args) {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => func.apply(this, args), delay);
  };
};

/**
 * Standardized search function for creative components
 * @param {Array} items - Array of items to search
 * @param {string} searchText - Search query
 * @param {string} itemType - Type of item ('media', 'adGroup')
 * @returns {Array} - Filtered items
 */
export const performCreativeSearch = (items, searchText, itemType = 'media') => {
  if (!searchText || !items || !Array.isArray(items)) {
    return items || [];
  }

  const searchWords = searchText.toLowerCase().split(" ").filter(word => word.trim());
  
  return items.filter(item => {
    if (!item) return false;

    const searchFields = [];

    if (itemType === 'media') {
      // For individual media items
      searchFields.push(
        item.name?.toLowerCase() || '',
        item.fileType?.toLowerCase() || '',
        item.type?.toLowerCase() || '',
        `${item.width || 0}x${item.height || 0}`,
        formatFileSize(item.size || 0).toLowerCase()
      );
      
      // Add date fields for media
      if (item.createdAt) {
        const date = new Date(item.createdAt);
        searchFields.push(
          date.toLocaleDateString().toLowerCase(), // MM/DD/YYYY format
          date.getFullYear().toString(), // Year
          (date.getMonth() + 1).toString().padStart(2, '0'), // Month (01-12)
          date.getDate().toString().padStart(2, '0') // Day (01-31)
        );
      }
      if (item.updatedAt) {
        const date = new Date(item.updatedAt);
        searchFields.push(
          date.toLocaleDateString().toLowerCase(),
          date.getFullYear().toString(),
          (date.getMonth() + 1).toString().padStart(2, '0'),
          date.getDate().toString().padStart(2, '0')
        );
      }
    } else if (itemType === 'adGroup') {
      // For ad groups
      searchFields.push(item.name?.toLowerCase() || '');
      
      // Add date fields for ad groups
      if (item.createdAt) {
        const date = new Date(item.createdAt);
        searchFields.push(
          date.toLocaleDateString().toLowerCase(),
          date.getFullYear().toString(),
          (date.getMonth() + 1).toString().padStart(2, '0'),
          date.getDate().toString().padStart(2, '0')
        );
      }
      if (item.updatedAt) {
        const date = new Date(item.updatedAt);
        searchFields.push(
          date.toLocaleDateString().toLowerCase(),
          date.getFullYear().toString(),
          (date.getMonth() + 1).toString().padStart(2, '0'),
          date.getDate().toString().padStart(2, '0')
        );
      }
      
      // Add media names and details from the ad group
      if (item.medias && Array.isArray(item.medias)) {
        item.medias.forEach(media => {
          searchFields.push(
            media.name?.toLowerCase() || '',
            media.fileType?.toLowerCase() || '',
            media.type?.toLowerCase() || '',
            `${media.width || 0}x${media.height || 0}`,
            formatFileSize(media.size || 0).toLowerCase()
          );
          
          // Add media dates
          if (media.createdAt) {
            const date = new Date(media.createdAt);
            searchFields.push(
              date.toLocaleDateString().toLowerCase(),
              date.getFullYear().toString(),
              (date.getMonth() + 1).toString().padStart(2, '0'),
              date.getDate().toString().padStart(2, '0')
            );
          }
        });
      }
    }

    // Check if all search words match at least one field
    return searchWords.every(word =>
      searchFields.some(field => field.includes(word))
    );
  });
};

/**
 * Constants for media validation
 */
export const MEDIA_CONSTANTS = {
  ALLOWED_IMAGE_TYPES: ["image/png", "image/jpeg", "image/jpg", "image/gif", "image/avif", "image/webp"],
  ALLOWED_VIDEO_TYPES: ["video/mp4"],
  MAX_FILE_SIZE_MB: 5, // Updated to 5MB limit
  VIDEO_DURATION_MIN: 15,
  VIDEO_DURATION_MAX: 30,
  S3_URL_EXPIRES: 600000,
  BULK_UPLOAD_CHUNK_SIZE: 10,
};

export default {
  transformFileName,
  getFileNameWithoutExtension,
  uploadToS3Storage,
  getVideoDuration,
  getVideoDimensions,
  getImageDimensions,
  validateFileSize,
  validateImageDimensions,
  validateVideoDuration,
  processMediasWithUrls,
  filterAdGroupsByMediaType,
  filterAdGroupsByMediaCompatibility,
  capitalizeFirstLetter,
  formatFileSize,
  handleMediaError,
  handleMediaSuccess,
  debounce,
  performCreativeSearch,
  MEDIA_CONSTANTS,
};